import { ZIndex } from '@opallabs/gem'
import { useTheme } from '@opallabs/hooks'
import {
  Popover,
  TextInputWithLabel,
  Button,
  ButtonSize,
} from '@opallabs/lapidary'
import * as styles from './styles'

export type EditUrlPopoverProps = {
  disabled?: boolean
  htmlId?: string
  initialLabel?: string | null
  initialUrl?: string
  label: string
  onCancel?: () => void
  onOpenChange?: (open: boolean) => void
  onSubmit: (value: { label: string | null; url: string }) => void
  open: boolean
  placeholderText?: string
  setOpen: (open: boolean) => void
  setLabel: (label: string) => void
  setUrl: (url: string) => void
  url: string
}

export const EditUrlPopover: React.FC<EditUrlPopoverProps> = ({
  disabled,
  htmlId,
  initialLabel,
  initialUrl,
  label,
  onCancel,
  onOpenChange,
  onSubmit,
  open,
  placeholderText,
  setLabel,
  setOpen,
  setUrl,
  url,
}) => {
  const theme = useTheme()
  const isDirty = initialUrl !== url || (initialLabel ?? '') !== label

  const handleSave: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault()
    if (!isDirty) return
    // Clear label if URL is empty
    if (!url) setLabel('')

    setOpen(false)
    onSubmit({
      url,
      label: label ?? null,
    })
  }

  const handleCancel = () => {
    setUrl(initialUrl ?? '')
    setLabel(initialLabel ?? '')

    setOpen(false)
    onCancel?.()
  }

  return (
    <Popover.Root
      open={open}
      onOpenChange={(open) => {
        setOpen(open)
        onOpenChange?.(open)
      }}
    >
      <Popover.Trigger disabled={disabled}>
        <Button
          ariaLabel="Edit Link"
          buttonText={label || url || placeholderText}
          css={styles.buttonOverrides({
            theme,
            showingPlaceholder: !label && !url,
          })}
          disabled={disabled}
          id={htmlId}
          name="URL Value"
          variant="ghostBlue"
        />
      </Popover.Trigger>
      <Popover.Content
        onFocusOutside={handleCancel}
        onEscapeKeyDown={handleCancel}
        zIndex={ZIndex.Drawer.Popover}
      >
        <form onSubmit={handleSave}>
          <div css={styles.popoverWrapper}>
            <TextInputWithLabel
              initialValue={url}
              labelText="URL"
              onChange={setUrl}
              styleOverrides={{
                label: styles.urlFieldLabel,
                input: styles.urlFieldInput,
              }}
              theme={theme}
            />
            <TextInputWithLabel
              labelText="Label"
              initialValue={label}
              onChange={setLabel}
              theme={theme}
            />
            <Button
              buttonText="Copy"
              variant="tertiary"
              css={styles.urlCopyButton}
              disabled={!url}
              name="Copy"
              onClick={() => url && navigator.clipboard.writeText(url)}
              size={ButtonSize.Small}
            />
          </div>
          <div css={styles.popoverFooter(theme)}>
            <Button
              buttonText="Cancel"
              variant="ghost"
              name="Cancel"
              onClick={handleCancel}
              size={ButtonSize.Small}
            />
            <Button
              buttonText="Save"
              variant="primary"
              css={{ width: 48 }} // Prevents variance when disabled
              name="Save"
              disabled={!isDirty}
              type="submit"
              size={ButtonSize.Small}
            />
          </div>
        </form>
      </Popover.Content>
    </Popover.Root>
  )
}
